import { useEffect, useMemo, useRef, useState } from 'react';
import type { ShiftGovernanceSettings, ShiftAvailabilityAccess, ShiftEmployeeLimits } from 'shared-models';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { availabilityDateIsLocked } from './availability-governance.utils';
import { notifications } from '@mantine/notifications';
import { shiftAvailabilityService } from '../../services/availability/shift-availability.service';
import type {
  ShiftAvailabilityDay,
  ShiftAvailabilityPeriod,
  ShiftAvailabilityWeek,
} from '../../models/availability.types';
import { getCurrentWeekStartIsoDate } from '../../utils/date.utils';
import { type AvailabilityDayUiState } from './availability-page.types';
import { resolveRequestErrorMessage } from '../../utils/request-errors.utils';

export function useAvailabilityPageController() {
  const { user } = useShiftAuth();
  const canManageGovernance = user?.role === 'manager' && user.canManageSchedule;
  const [weekStartDate, setWeekStartDate] = useState<string>(() => getCurrentWeekStartIsoDate());
  const [week, setWeek] = useState<ShiftAvailabilityWeek | null>(null);
  const [isLoadingWeek, setIsLoadingWeek] = useState(true);
  const [weekError, setWeekError] = useState<string | null>(null);
  const [noteContent, setNoteContent] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [dayUiState, setDayUiState] = useState<Record<string, AvailabilityDayUiState>>({});
  const [clock, setClock] = useState(Date.now);
  const [governanceDraft, setGovernanceDraft] = useState<Partial<Omit<ShiftGovernanceSettings, 'revision'>>>({});
  const [isSavingGovernance, setIsSavingGovernance] = useState(false);
  const [governanceError, setGovernanceError] = useState<string | null>(null);
  const governanceLock = useRef(false);
  const draftRevision = useRef<number | null>(null);
  const latestSettings = useRef<ShiftGovernanceSettings | null>(null);
  const latestLocks = useRef(new Map<string, ShiftAvailabilityAccess>());
  const latestLimits = useRef(new Map<number,ShiftEmployeeLimits>());
  const [weeklyLimitsOutdated,setWeeklyLimitsOutdated] = useState(false);
  const limitsRefreshLock = useRef(false);
  const applyGovernance = (payload: ShiftAvailabilityWeek): ShiftAvailabilityWeek => {
    const status = payload.weeklyLimits;
    if (status && status.limits.revision > (latestLimits.current.get(status.limits.employeePk)?.revision ?? -1))
      latestLimits.current.set(status.limits.employeePk,status.limits);
    const limits = status ? latestLimits.current.get(status.limits.employeePk) : undefined;
    if (payload.governance && payload.governance.revision > (latestSettings.current?.revision ?? -1))
      latestSettings.current = payload.governance;
    if (payload.access && payload.access.lockRevision > (latestLocks.current.get(payload.weekStartDate)?.lockRevision ?? -1))
      latestLocks.current.set(payload.weekStartDate, payload.access);
    const settings = latestSettings.current;
    const lock = latestLocks.current.get(payload.weekStartDate);
    return { ...payload,
      ...(status && limits ? {weeklyLimits:{...status,limits}} : {}),
      ...(settings && settings.revision >= (payload.governance?.revision ?? 0) ? { governance: settings } : {}),
      ...(lock && lock.lockRevision >= (payload.access?.lockRevision ?? 0) ? { access: lock } : {}),
    };
  };
  useShiftEvents(event => {
    if (event.type === 'governance.changed') {
      if ((latestSettings.current?.revision ?? -1) >= event.settings.revision) return;
      latestSettings.current = event.settings;
      setWeek(current => current ? applyGovernance(current) : current);
    } else if (event.type === 'availability.lockChanged') {
      if ((latestLocks.current.get(event.access.weekStart)?.lockRevision ?? -1) >= event.access.lockRevision) return;
      latestLocks.current.set(event.access.weekStart, event.access);
      setWeek(current => current ? applyGovernance(current) : current);
    } else if (event.type === 'employeeLimits.upserted') {
      if ((latestLimits.current.get(event.setting.employeePk)?.revision ?? -1) >= event.setting.revision) return;
      latestLimits.current.set(event.setting.employeePk,event.setting);
      setWeek(current => current ? applyGovernance(current) : current);
    } else if (event.type === 'schedule.publicationChanged' && event.weekStartDate === week?.weekStartDate) {
      setWeeklyLimitsOutdated(true);
    } else if (event.type === 'sse.resync') void loadWeek(weekStartDate);
  });
  useEffect(() => {
    if (week?.governance?.deadlineWeekday == null || user?.role === 'manager') return;
    const timer = window.setTimeout(() => setClock(Date.now()), 60000 - Date.now() % 60000);
    return () => window.clearTimeout(timer);
  }, [clock, week?.governance?.deadlineWeekday, week?.governance?.deadlineTime, user?.role]);
  const canEditDate = (dateDay: string) => !availabilityDateIsLocked(dateDay, week?.weekStartDate ?? weekStartDate,
    week?.governance, week?.access, user?.role, clock);
  const canEditNotes = canEditDate(week?.weekStartDate ?? weekStartDate);
  const setGovernanceField = <Key extends keyof Omit<ShiftGovernanceSettings, 'revision'>>(key: Key, value: ShiftGovernanceSettings[Key]) => {
    if (draftRevision.current === null) draftRevision.current = week?.governance?.revision ?? 0;
    setGovernanceDraft(current => ({ ...current, [key]: value }));
  };
  const resetGovernanceDraft = () => { setGovernanceDraft({}); draftRevision.current = null; setGovernanceError(null); };
  const governanceConflict = draftRevision.current !== null && draftRevision.current !== week?.governance?.revision;
  const changeGovernance = async (intent: 'save' | 'weekLock' | 'unlockHistory') => {
    if (!canManageGovernance || !week?.governance || !week.access || governanceLock.current || isWeekBusy) return;
    governanceLock.current = true;
    setIsSavingGovernance(true);
    setGovernanceError(null);
    try {
      if (intent === 'weekLock') await shiftAvailabilityService.patchWeekLock(week.weekStartDate, {
        isLocked: !week.access.isManuallyLocked, expectedRevision: week.access.lockRevision,
      });
      else if (intent === 'unlockHistory') {
        if (!week.governance.historicalLockDate || !window.confirm('לפתוח את הנתונים ההיסטוריים לעריכה?')) return;
        await shiftAvailabilityService.patchGovernance({ historicalLockDate: null, expectedRevision: week.governance.revision });
      } else {
        if (governanceConflict) { setGovernanceError('ההגדרות השתנו. אפסו את העריכה ונסו שוב.'); return; }
        const changes = Object.fromEntries(Object.entries(governanceDraft).filter(([key, value]) => value !== week.governance![key as keyof ShiftGovernanceSettings]));
        if (!Object.keys(changes).length) { resetGovernanceDraft(); return; }
        if (week.governance.historicalLockDate && 'historicalLockDate' in changes
          && (!changes.historicalLockDate || changes.historicalLockDate < week.governance.historicalLockDate)) {
          setGovernanceError('יש לפתוח את הנתונים ההיסטוריים לפני הקטנת טווח הנעילה.');
          return;
        }
        await shiftAvailabilityService.patchGovernance({ ...changes, expectedRevision: week.governance.revision });
      }
      resetGovernanceDraft();
    } catch (error) { setGovernanceError(resolveRequestErrorMessage(error, 'שמירת ההגדרות נכשלה.')); }
    finally { governanceLock.current = false; setIsSavingGovernance(false); }
  };

  const dayLocksRef = useRef<Set<string>>(new Set());

  const noteMutationLockRef = useRef(false);

  const weekLoadRequestIdRef = useRef(0);
  const isWeekBusy = isLoadingWeek || isSavingNote || isSavingGovernance || Object.values(dayUiState).some((state) => state.isSaving);

  const loadWeek = async (targetWeekStart: string) => {
    const requestId = weekLoadRequestIdRef.current + 1;
    weekLoadRequestIdRef.current = requestId;
    setIsLoadingWeek(true);
    setWeekError(null);

    try {
      const payload = await shiftAvailabilityService.getWeek(targetWeekStart);
      if (weekLoadRequestIdRef.current !== requestId) {
        return;
      }

      setWeek(applyGovernance(payload));
      setWeeklyLimitsOutdated(false);
      setNoteContent(payload.weeklyNote.content);
      setDayUiState({});
    } catch (requestError: unknown) {
      if (weekLoadRequestIdRef.current !== requestId) {
        return;
      }

      setWeekError(resolveRequestErrorMessage(requestError, 'לא ניתן לטעון את הזמינות.'));
    } finally {
      if (weekLoadRequestIdRef.current === requestId) {
        setIsLoadingWeek(false);
      }
    }
  };

  useEffect(() => {
    resetGovernanceDraft();
    void loadWeek(weekStartDate);
  }, [weekStartDate]);

  const periodRows = useMemo(() => {
    const rows = new Map<number | null, ShiftAvailabilityPeriod>();
    week?.days.forEach((day) =>
      day.periods.forEach((period) => {
        if (!rows.has(period.sectionShiftsPk)) {
          rows.set(period.sectionShiftsPk, period);
        }
      }),
    );
    return Array.from(rows.values());
  }, [week]);

  const weeklyLimitCounts = week?.weeklyLimits ? { ...week.weeklyLimits.counts,
    minimumWeekdayRequests:week.days.reduce((total,day) => total + (new Date(`${day.dateDay}T12:00:00Z`).getUTCDay() < 5
      ? day.periods.filter(period => period.isAvailable).length : 0),0),
    minimumWeekendRequests:week.days.reduce((total,day) => total + (new Date(`${day.dateDay}T12:00:00Z`).getUTCDay() >= 5
      ? day.periods.filter(period => period.isAvailable).length : 0),0),
  } : undefined;
  const refreshWeeklyLimits = async () => {
    if (isWeekBusy || limitsRefreshLock.current) return;
    limitsRefreshLock.current=true;
    try {await loadWeek(week?.weekStartDate ?? weekStartDate);} finally {limitsRefreshLock.current=false;}
  };

  const onTogglePeriod = async (day: ShiftAvailabilityDay, period: ShiftAvailabilityPeriod) => {
    const stateKey = `${day.dateDay}:${period.sectionShiftsPk}`;
    if (!week || !canEditDate(day.dateDay) || governanceLock.current || isLoadingWeek || noteMutationLockRef.current || dayLocksRef.current.has(stateKey)) {
      return;
    }

    dayLocksRef.current.add(stateKey);
    const nextIsAvailable = !period.isAvailable;

    setDayUiState((current) => ({
      ...current,
      [stateKey]: {
        isSaving: true,
        error: null,
      },
    }));

    setWeek((current) => {
      if (!current) {
        return current;
      }

      return {
        ...current,
        days: current.days.map((candidateDay) =>
          candidateDay.dateDay === day.dateDay
            ? {
                ...candidateDay,
                periods: candidateDay.periods.map((candidatePeriod) =>
                  candidatePeriod.sectionShiftsPk === period.sectionShiftsPk
                    ? { ...candidatePeriod, isAvailable: nextIsAvailable }
                    : candidatePeriod,
                ),
              }
            : candidateDay,
        ),
      };
    });

    try {
      const updated = await shiftAvailabilityService.updateDay(day.dateDay, {
        sectionShiftsPk: period.sectionShiftsPk,
        isAvailable: nextIsAvailable,
      });
      setWeek((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          days: current.days.map((candidateDay) =>
            candidateDay.dateDay === day.dateDay
              ? {
                  ...candidateDay,
                  periods: candidateDay.periods.map((candidatePeriod) =>
                    candidatePeriod.sectionShiftsPk === period.sectionShiftsPk
                      ? { ...candidatePeriod, isAvailable: updated.isAvailable }
                      : candidatePeriod,
                  ),
                }
              : candidateDay,
          ),
        };
      });

      setDayUiState((current) => ({
        ...current,
        [stateKey]: {
          isSaving: false,
          error: null,
        },
      }));
      notifications.show({ color: 'green', message: 'הזמינות נשמרה בהצלחה.' });
    } catch (requestError: unknown) {
      setDayUiState((current) => ({
        ...current,
        [stateKey]: {
          isSaving: false,
          error: resolveRequestErrorMessage(requestError, 'שמירת הזמינות נכשלה.'),
        },
      }));

      setWeek((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          days: current.days.map((candidateDay) =>
            candidateDay.dateDay === day.dateDay
              ? {
                  ...candidateDay,
                  periods: candidateDay.periods.map((candidatePeriod) =>
                    candidatePeriod.sectionShiftsPk === period.sectionShiftsPk
                      ? { ...candidatePeriod, isAvailable: period.isAvailable }
                      : candidatePeriod,
                  ),
                }
              : candidateDay,
          ),
        };
      });
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: resolveRequestErrorMessage(requestError, 'שמירת הזמינות נכשלה.'),
      });

      try {
        const refreshed = await shiftAvailabilityService.getWeek(weekStartDate);
        setWeek(applyGovernance(refreshed));
      } catch {
        setWeekError('שמירת הזמינות נכשלה ולא ניתן לרענן את הנתונים כרגע.');
      }
    } finally {
      dayLocksRef.current.delete(stateKey);
    }
  };

  const mutateWeeklyNotes = async (operation: 'saveWeek' | 'saveDefault' | 'deleteDefault') => {
    if (!week || !canEditNotes || governanceLock.current || isLoadingWeek || dayLocksRef.current.size > 0 || noteMutationLockRef.current) {
      return;
    }

    noteMutationLockRef.current = true;
    setIsSavingNote(true);
    try {
      if (operation === 'deleteDefault') {
        await shiftAvailabilityService.mutateWeeklyNotes({ operation, weekStart: week.weekStartDate });
      } else {
        await shiftAvailabilityService.mutateWeeklyNotes({
          operation,
          weekStart: week.weekStartDate,
          content: noteContent,
        });
      }
      await loadWeek(week.weekStartDate);
      notifications.show({ color: 'green', message: operation === 'saveWeek'
        ? 'ההערות נשמרו בהצלחה.'
        : operation === 'saveDefault' ? 'ברירת המחדל נשמרה בהצלחה.' : 'ברירת המחדל נמחקה בהצלחה.' });
    } catch (requestError: unknown) {
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: resolveRequestErrorMessage(requestError, 'שמירת ההערות נכשלה.'),
      });
    } finally {
      noteMutationLockRef.current = false;
      setIsSavingNote(false);
    }
  };

  return {
    weeklyLimitCounts,weeklyLimitsOutdated,refreshWeeklyLimits,
    canManageGovernance, governanceDraft, governanceError, governanceConflict, isSavingGovernance,
    setGovernanceField, resetGovernanceDraft, changeGovernance, canEditDate, canEditNotes,
    weekStartDate,
    setWeekStartDate,
    week,
    isLoadingWeek,
    weekError,
    noteContent,
    setNoteContent,
    isSavingNote,
    isWeekBusy,
    dayUiState,
    loadWeek,
    periodRows,
    onTogglePeriod,
    mutateWeeklyNotes,
  };
}
